import {
  reconcileEnrichmentRelationshipPairs,
  validateEnrichmentRelationshipPairs,
} from "../services/api";
import { augmentRelMissingEndpoint } from "./enrichmentMissingEndpoint";
import {
  applyMetamodelReconcileToRelationship,
  guessInstantRelationshipType,
  relationshipTypeChangedForMetamodel,
  shouldFlipRelationshipForPeerType,
} from "./enrichmentRelationshipMetamodel";

export function normName(s) {
  return String(s || "")
    .trim()
    .toLowerCase();
}

function missingEntityKey(surfaceName, entityType) {
  const ms = normName(surfaceName);
  const mt = normName(entityType);
  if (!ms || !mt) return null;
  return `missing:${ms}|${mt}`;
}

export function parseRosterOverrideKey(key) {
  const raw = String(key || "").trim();
  const match = raw.match(/^(missing|resolution):([^|]+)\|(.+)$/);
  if (!match) return null;
  return {
    kind: match[1],
    surfaceNorm: match[2],
    typeNorm: match[3],
  };
}

function rosterOverrideKeyForSurfaceType(preview, surfaceName, entityType) {
  const pk = `${normName(surfaceName)}|${normName(entityType)}`;
  if (!normName(surfaceName) || !normName(entityType)) return null;
  const resolution = Array.isArray(preview?.resolution) ? preview.resolution : [];
  const hasResolutionRow = resolution.some((row) => {
    if (!row || row.kind === "linked_existing") return false;
    const surface = String(row.surface_name || "").trim();
    const et = String(row.entity_type || "").trim();
    return `${normName(surface)}|${normName(et)}` === pk;
  });
  return hasResolutionRow ? `resolution:${pk}` : missingEntityKey(surfaceName, entityType);
}

/**
 * After a catalog type correction, keep resolution rows, overrides, and proposed
 * relationship gaps aligned so review cards stay visible and "Choose existing" works.
 */
export function applyEntityTypeOverrideSideEffects(
  preview,
  { rosterKey, suggestedType, newType }
) {
  if (!preview) return preview;
  const parsed = parseRosterOverrideKey(rosterKey);
  if (!parsed) return preview;

  const nextType = String(newType || "").trim();
  const oldType = String(suggestedType || parsed.typeNorm || "").trim();
  if (
    !nextType ||
    normalizeTypeForCompare(nextType) === normalizeTypeForCompare(oldType)
  ) {
    return preview;
  }

  const { surfaceNorm } = parsed;
  const outcomes = { ...(preview.enrichment_review_outcomes || {}) };
  const overrides = { ...(outcomes.entity_type_overrides || {}) };
  const overrideValue = overrides[rosterKey] ?? nextType;

  let surfaceDisplayName = "";
  const resolution = Array.isArray(preview.resolution) ? preview.resolution : [];
  resolution.forEach((row) => {
    if (normName(row?.surface_name) === surfaceNorm && !surfaceDisplayName) {
      surfaceDisplayName = String(row.surface_name || "").trim();
    }
  });

  const nextResolution = resolution.map((row) => {
    if (!row || row.kind === "linked_existing") return row;
    if (normName(row.surface_name) !== surfaceNorm) return row;
    const rowType = normName(row.entity_type);
    if (
      rowType !== normName(oldType) &&
      rowType !== parsed.typeNorm &&
      normalizeTypeForCompare(row.entity_type) !==
        normalizeTypeForCompare(oldType)
    ) {
      return row;
    }
    if (!surfaceDisplayName) {
      surfaceDisplayName = String(row.surface_name || "").trim();
    }
    return { ...row, entity_type: nextType };
  });

  const nextRelationships = (Array.isArray(preview.proposed_relationships)
    ? preview.proposed_relationships
    : []
  ).map((rel) => {
    const row = augmentRelMissingEndpoint(rel);
    if (normName(row.missing_surface_name) !== surfaceNorm) return rel;
    const missingType = normName(row.missing_entity_type);
    if (
      missingType !== normName(oldType) &&
      missingType !== parsed.typeNorm &&
      normalizeTypeForCompare(row.missing_entity_type) !==
        normalizeTypeForCompare(oldType)
    ) {
      return rel;
    }
    const next = { ...row, missing_entity_type: nextType };
    if (row.missing_end === "source") {
      next.source_entity_type = nextType;
    } else if (row.missing_end === "target") {
      next.target_entity_type = nextType;
    }
    return augmentRelMissingEndpoint(next);
  });

  const typeMatchesOld = (value) => {
    const t = String(value || "").trim();
    if (!t) return false;
    return (
      normName(t) === normName(oldType) ||
      normName(t) === parsed.typeNorm ||
      normalizeTypeForCompare(t) === normalizeTypeForCompare(oldType)
    );
  };

  const nextCatalog = (Array.isArray(preview.proposed_catalog_entities)
    ? preview.proposed_catalog_entities
    : []
  ).map((row) => {
    if (!row || typeof row !== "object") return row;
    let next = row;
    let changed = false;
    const patch = (mutator) => {
      if (!changed) next = { ...row };
      changed = true;
      mutator(next);
    };

    if (
      normName(row.provider_application_name) === surfaceNorm &&
      typeMatchesOld(row.provider_entity_type || "application")
    ) {
      patch((r) => {
        r.provider_entity_type = nextType;
      });
    }
    if (
      normName(row.consumer_application_name) === surfaceNorm &&
      typeMatchesOld(row.consumer_entity_type || "application")
    ) {
      patch((r) => {
        r.consumer_entity_type = nextType;
      });
    }
    if (
      normName(row.implementation_peer_name) === surfaceNorm ||
      normName(row.technology_component_name) === surfaceNorm ||
      normName(row.platform_name) === surfaceNorm
    ) {
      const peerType =
        row.implementation_peer_type ||
        (row.platform_name ? "platform" : "technology-component");
      if (typeMatchesOld(peerType)) {
        patch((r) => {
          r.implementation_peer_type = nextType;
          if (nextType === "platform") {
            if (r.technology_component_name && !r.platform_name) {
              r.platform_name = r.technology_component_name;
            }
            if (!r.implementation_peer_name) {
              r.implementation_peer_name =
                r.platform_name || r.technology_component_name;
            }
          } else if (nextType === "technology-component") {
            if (r.platform_name && !r.technology_component_name) {
              r.technology_component_name = r.platform_name;
            }
            if (!r.implementation_peer_name) {
              r.implementation_peer_name =
                r.technology_component_name || r.platform_name;
            }
          }
        });
      }
    }
    return changed ? next : row;
  });

  const keysToClear = new Set([rosterKey]);
  Object.keys(overrides).forEach((key) => {
    const keyParsed = parseRosterOverrideKey(key);
    if (!keyParsed || keyParsed.surfaceNorm !== surfaceNorm) return;
    keysToClear.add(key);
  });

  const canonicalSurface = surfaceDisplayName || surfaceNorm;
  const nextKey = rosterOverrideKeyForSurfaceType(
    { ...preview, resolution: nextResolution },
    canonicalSurface,
    nextType
  );
  keysToClear.forEach((key) => {
    delete overrides[key];
  });
  if (nextKey) {
    overrides[nextKey] = overrideValue;
  }

  // Keep resolution_matches / roster approvals keyed to the new type.
  const migrateOutcomeMap = (map) => {
    if (!map || typeof map !== "object") return map;
    const nextMap = { ...map };
    let mapChanged = false;
    Object.keys(map).forEach((key) => {
      const sep = key.lastIndexOf("|");
      if (sep <= 0) return;
      const namePart = key.slice(0, sep);
      const typePart = key.slice(sep + 1);
      if (normName(namePart) !== surfaceNorm) return;
      if (
        normName(typePart) !== normName(oldType) &&
        normName(typePart) !== parsed.typeNorm &&
        normalizeTypeForCompare(typePart) !== normalizeTypeForCompare(oldType)
      ) {
        return;
      }
      const nextOutcomeKey = `${namePart}|${normName(nextType)}`;
      if (nextOutcomeKey === key) return;
      if (nextMap[nextOutcomeKey] == null) {
        nextMap[nextOutcomeKey] = map[key];
      }
      delete nextMap[key];
      mapChanged = true;
    });
    return mapChanged ? nextMap : map;
  };

  const resolutionMatches = migrateOutcomeMap(outcomes.resolution_matches);
  const rosterApprovals = migrateOutcomeMap(outcomes.entity_roster_approvals);

  return {
    ...preview,
    resolution: nextResolution,
    proposed_relationships: nextRelationships,
    proposed_catalog_entities: nextCatalog,
    enrichment_review_outcomes: {
      ...outcomes,
      entity_type_overrides: overrides,
      ...(resolutionMatches !== outcomes.resolution_matches
        ? { resolution_matches: resolutionMatches }
        : {}),
      ...(rosterApprovals !== outcomes.entity_roster_approvals
        ? { entity_roster_approvals: rosterApprovals }
        : {}),
    },
  };
}

export function resolutionKey(row) {
  return `${normName(row?.surface_name)}|${normName(row?.entity_type)}`;
}

/**
 * Entity type from a steward-decided resolution row for a surface name
 * (kept_new / confirmed / linked_existing with id).
 */
export function resolutionDecidedType(preview, surfaceName) {
  const sn = normName(surfaceName);
  if (!sn || !preview) return null;
  const rows = Array.isArray(preview?.resolution) ? preview.resolution : [];
  const outcomes = preview?.enrichment_review_outcomes?.resolution_matches || {};

  const isRowDecided = (row) => {
    if (!row || normName(row.surface_name) !== sn) return false;
    const rk = resolutionKey(row);
    const st = outcomes[rk];
    if (st === "discarded") return false;
    if (st === "kept_new" || st === "confirmed") return true;
    const kind = String(row?.kind || "").trim();
    if (kind === "linked_existing" && String(row?.entity_id || "").trim()) {
      return true;
    }
    if (
      kind === "create_new" &&
      String(row?.reason_code || "") === "user_kept_as_new"
    ) {
      return true;
    }
    return false;
  };

  const decided = rows.filter(isRowDecided);
  if (!decided.length) return null;

  const appRow = decided.find(
    (r) => normalizeTypeForCompare(r.entity_type) === "application"
  );
  const pick = appRow || decided[0];
  return normalizeTypeForCompare(pick?.entity_type) || null;
}

/** Anchor application under enrichment — already typed before the job runs. */
export function enrichmentAnchorContext(preview, editingEntity = null) {
  const anchorId = String(
    preview?.anchor_application_id || editingEntity?.id || ""
  ).trim();
  const anchorName = normName(
    (typeof editingEntity?.name === "string" && editingEntity.name.trim()) ||
      preview?.anchor_name ||
      ""
  );
  const anchorType = normalizeTypeForCompare(
    editingEntity?.type ||
      editingEntity?.entity_type ||
      preview?.anchor_entity_type ||
      "application"
  );
  return { anchorId, anchorName, anchorType };
}

/**
 * True when a surface name / type / id refers to the anchor application (not a new entity).
 */
export function isEnrichmentAnchorEntity(
  preview,
  editingEntity,
  { surfaceName = "", entityType = "", entityId = "" } = {}
) {
  const { anchorId, anchorName, anchorType } = enrichmentAnchorContext(
    preview,
    editingEntity
  );
  const id = String(entityId || "").trim();
  if (anchorId && id && id === anchorId) return true;
  const surface = normName(surfaceName);
  if (!surface || !anchorName || surface !== anchorName) return false;
  const et = normalizeTypeForCompare(entityType);
  if (!et || et === "application" || et === anchorType) return true;
  return false;
}

/**
 * Effective entity type for a named catalog / interface endpoint after steward
 * resolution and roster overrides (ignores stale application defaults on catalog rows).
 */
export function effectiveCatalogSurfaceType(
  preview,
  surfaceName,
  suggestedBaseType = "application",
  editingEntity = null
) {
  const overrides = preview?.enrichment_review_outcomes?.entity_type_overrides || {};
  const surface = String(surfaceName || "").trim();
  const base = String(suggestedBaseType || "application").trim();
  if (!surface) return normalizeTypeForCompare(base) || "application";

  if (
    isEnrichmentAnchorEntity(preview, editingEntity, {
      surfaceName: surface,
      entityType: base,
    })
  ) {
    return normalizeTypeForCompare(
      editingEntity?.type ||
        editingEntity?.entity_type ||
        preview?.anchor_entity_type ||
        "application"
    );
  }

  const decidedType = resolutionDecidedType(preview, surface);

  const tryTypes = [...new Set([decidedType, base].filter(Boolean))];
  for (const t of tryTypes) {
    const pk = `${normName(surface)}|${normName(t)}`;
    if (overrides[`resolution:${pk}`]) {
      return normalizeTypeForCompare(overrides[`resolution:${pk}`]);
    }
    const mk = missingEntityKey(surface, t);
    if (mk && overrides[mk]) {
      return normalizeTypeForCompare(overrides[mk]);
    }
  }

  return normalizeTypeForCompare(decidedType || base) || "application";
}

/**
 * Roster / override map key for a missing relationship endpoint (aligns with
 * {@link buildEnrichmentEntityRoster} and {@link effectiveEndpointTypesForValidation}).
 */
export function rosterTypeOverrideKeyForGap(
  preview,
  missingSurfaceName,
  missingEntityType,
  editingEntity = null
) {
  if (
    isEnrichmentAnchorEntity(preview, editingEntity, {
      surfaceName: missingSurfaceName,
      entityType: missingEntityType,
    })
  ) {
    return null;
  }
  const effectiveType = effectiveCatalogSurfaceType(
    preview,
    missingSurfaceName,
    missingEntityType,
    editingEntity
  );
  const pk = `${normName(missingSurfaceName)}|${normName(effectiveType)}`;
  if (!normName(missingSurfaceName) || !normName(effectiveType)) return null;
  const resolution = Array.isArray(preview?.resolution) ? preview.resolution : [];
  const hasResolutionRow = resolution.some((row) => {
    if (!row || row.kind === "linked_existing") return false;
    const surface = String(row.surface_name || "").trim();
    const et = String(row.entity_type || "").trim();
    return `${normName(surface)}|${normName(et)}` === pk;
  });
  if (hasResolutionRow) return `resolution:${pk}`;
  return missingEntityKey(missingSurfaceName, effectiveType);
}

/**
 * Build roster rows for entities that still need catalog type confirmation:
 * resolution candidates and distinct missing endpoints from proposed relationships.
 * The anchor application is excluded — it is chosen/created as an application before enrichment runs.
 */
export function buildEnrichmentEntityRoster(preview, editingEntity) {
  const entries = [];
  const seen = new Set();

  const pushEntry = (key, label, suggestedType, meta) => {
    if (!key || seen.has(key)) return;
    seen.add(key);
    entries.push({
      key,
      label,
      suggestedType: suggestedType || "application",
      ...meta,
    });
  };

  const resolution = Array.isArray(preview?.resolution) ? preview.resolution : [];
  resolution.forEach((row, resolutionIndex) => {
    if (!row || row.kind === "linked_existing") return;
    const surface = String(row.surface_name || "").trim();
    const et = String(row.entity_type || "").trim();
    if (!surface || !et) return;
    if (
      isEnrichmentAnchorEntity(preview, editingEntity, {
        surfaceName: surface,
        entityType: et,
        entityId: row.entity_id || row.linked_entity_id,
      })
    ) {
      return;
    }
    const rk = resolutionKey(row);
    pushEntry(`resolution:${rk}`, `${surface}`, et, {
      category: "resolution",
      resolutionIndex,
      subtitle: et,
    });
  });

  const proposed = Array.isArray(preview?.proposed_relationships)
    ? preview.proposed_relationships
    : [];
  /** Same surface+type already has a resolution roster row — do not add a duplicate missing-endpoint row. */
  const rosterHasResolutionForMissing = (surface, entityType) => {
    const pk = `${normName(surface)}|${normName(entityType)}`;
    return Boolean(pk && seen.has(`resolution:${pk}`));
  };
  proposed.forEach((rel) => {
    const r = augmentRelMissingEndpoint(rel);
    const sid = String(r.source_entity_id || "").trim();
    const tid = String(r.target_entity_id || "").trim();
    if (!sid && r.missing_end === "source") {
      if (
        isEnrichmentAnchorEntity(preview, editingEntity, {
          surfaceName: r.missing_surface_name,
          entityType: r.missing_entity_type,
        })
      ) {
        return;
      }
      const mk = missingEntityKey(r.missing_surface_name, r.missing_entity_type);
      if (
        mk &&
        !rosterHasResolutionForMissing(r.missing_surface_name, r.missing_entity_type)
      ) {
        pushEntry(
          mk,
          String(r.missing_surface_name || "").trim(),
          String(r.missing_entity_type || "").trim(),
          { category: "missing_endpoint", end: "source" }
        );
      }
    }
    if (!tid && r.missing_end === "target") {
      if (
        isEnrichmentAnchorEntity(preview, editingEntity, {
          surfaceName: r.missing_surface_name,
          entityType: r.missing_entity_type,
        })
      ) {
        return;
      }
      const mk = missingEntityKey(r.missing_surface_name, r.missing_entity_type);
      if (
        mk &&
        !rosterHasResolutionForMissing(r.missing_surface_name, r.missing_entity_type)
      ) {
        pushEntry(
          mk,
          String(r.missing_surface_name || "").trim(),
          String(r.missing_entity_type || "").trim(),
          { category: "missing_endpoint", end: "target" }
        );
      }
    }
  });

  return entries;
}

export function normalizeTypeForCompare(t) {
  return String(t || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/_/g, "-");
}

function normalizeType(t) {
  return normalizeTypeForCompare(t);
}

/** Apply roster type overrides for a named endpoint (missing, resolution, or linked id). */
function resolveTypeWithOverrides(overrides, { surfaceName = "", baseType = "" } = {}) {
  const surface = String(surfaceName || "").trim();
  const base = String(baseType || "").trim();
  if (!surface && !base) return "";
  const pk = `${normName(surface)}|${normName(base)}`;
  const resK = pk && normName(base) ? `resolution:${pk}` : "";
  const mk = surface && base ? missingEntityKey(surface, base) : null;
  const raw =
    (resK && overrides[resK]) ||
    (mk && overrides[mk]) ||
    base ||
    "";
  return normalizeType(raw);
}

/**
 * Effective source/target types for metamodel pair validation (after roster overrides).
 */
export function effectiveEndpointTypesForValidation(
  rel,
  overrides = {},
  preview = {},
  editingEntity = {}
) {
  const r = augmentRelMissingEndpoint(rel);
  const anchorId = String(
    preview?.anchor_application_id || editingEntity?.id || ""
  ).trim();
  const sid = String(r.source_entity_id || "").trim();
  const tid = String(r.target_entity_id || "").trim();

  let sourceType = "";
  let targetType = "";

  if (sid && anchorId && sid === anchorId) {
    sourceType =
      overrides.anchor ||
      editingEntity?.type ||
      editingEntity?.entity_type ||
      "";
  } else if (sid) {
    sourceType = resolveTypeWithOverrides(overrides, {
      surfaceName:
        r.missing_surface_name ||
        r.surface_source_name ||
        r.surface_other_application ||
        "",
      baseType: r.source_entity_type || r.missing_entity_type,
    });
  } else if (r.missing_end === "source") {
    sourceType = resolveTypeWithOverrides(overrides, {
      surfaceName: r.missing_surface_name,
      baseType: r.missing_entity_type,
    });
  }

  if (tid && anchorId && tid === anchorId) {
    targetType =
      overrides.anchor ||
      editingEntity?.type ||
      editingEntity?.entity_type ||
      "";
  } else if (tid) {
    targetType = resolveTypeWithOverrides(overrides, {
      surfaceName:
        r.missing_surface_name ||
        r.surface_target_name ||
        r.surface_other_application ||
        r.surface_child_application ||
        "",
      baseType: r.target_entity_type || r.missing_entity_type,
    });
  } else if (r.missing_end === "target") {
    targetType = resolveTypeWithOverrides(overrides, {
      surfaceName: r.missing_surface_name,
      baseType: r.missing_entity_type,
    });
  }

  sourceType = normalizeType(sourceType);
  targetType = normalizeType(targetType);

  return {
    sourceType,
    targetType,
    canValidate: Boolean(sourceType && targetType),
  };
}

/** Entity type to use when auto-creating a missing endpoint (respects roster overrides). */
export function missingEndpointEntityTypeForCreate(
  rel,
  preview = {},
  editingEntity = null
) {
  const r = augmentRelMissingEndpoint(rel);
  const overrides = preview?.enrichment_review_outcomes?.entity_type_overrides || {};
  const { sourceType, targetType } = effectiveEndpointTypesForValidation(
    r,
    overrides,
    preview,
    editingEntity
  );
  if (r.missing_end === "source") {
    return sourceType || String(r.missing_entity_type || "").trim();
  }
  if (r.missing_end === "target") {
    return targetType || String(r.missing_entity_type || "").trim();
  }
  return String(r.missing_entity_type || "").trim();
}

/** Proposed relationship indices whose missing endpoint matches a roster entity (by surface). */
export function proposedIndicesTouchingRosterKey(key, proposed, preview, editingEntity) {
  const idxs = new Set();
  const proposedArr = Array.isArray(proposed) ? proposed : [];

  if (key === "anchor") {
    return [];
  }

  const parsed = parseRosterOverrideKey(key);
  const surfaceNorm = parsed?.surfaceNorm;
  if (!surfaceNorm) return [];

  if (key.startsWith("missing:") || key.startsWith("resolution:")) {
    proposedArr.forEach((rel, idx) => {
      const r = augmentRelMissingEndpoint(rel);
      if (String(r.source_entity_id || "").trim() && String(r.target_entity_id || "").trim()) {
        return;
      }
      if (normName(r.missing_surface_name) !== surfaceNorm) return;
      idxs.add(idx);
    });
    return Array.from(idxs);
  }

  return [];
}

function relationshipRowDecidedForMetamodel(preview, rel, idx, relActions = {}) {
  if (String(rel?.existing_relationship_id || "").trim()) return true;
  const key = `p${idx}`;
  const live = relActions[key]?.state;
  if (
    live === "approved" ||
    live === "discarded" ||
    live === "approving" ||
    live === "discarding"
  ) {
    return true;
  }
  const po = preview?.enrichment_review_outcomes?.proposed_relationships?.[key];
  return po === "approved" || po === "discarded";
}

/**
 * Pending proposed relationships with effective endpoint types for metamodel validation.
 */
export function collectPendingRelationshipMetamodelPairs(
  preview,
  overrides = {},
  editingEntity = {},
  relActions = {}
) {
  const rows = Array.isArray(preview?.proposed_relationships)
    ? preview.proposed_relationships
    : [];
  const pairs = [];
  const pairIndices = [];

  rows.forEach((rel, idx) => {
    if (!rel || relationshipRowDecidedForMetamodel(preview, rel, idx, relActions)) {
      return;
    }
    const { sourceType, targetType, canValidate } = effectiveEndpointTypesForValidation(
      rel,
      overrides,
      preview,
      editingEntity
    );
    if (!canValidate) return;
    pairs.push({ source_type: sourceType, target_type: targetType });
    pairIndices.push(idx);
  });

  return { pairs, pairIndices };
}

/**
 * Synchronously repair one proposed relationship row for immediate UI feedback
 * (before reconcile API). Returns updated preview or unchanged preview.
 */
export function applyInstantMetamodelForRelationship(
  preview,
  overrides = {},
  editingEntity = {},
  relIdx
) {
  const idx = Number(relIdx);
  if (!preview || !Number.isFinite(idx) || idx < 0) return preview;
  const rows = Array.isArray(preview.proposed_relationships)
    ? preview.proposed_relationships
    : [];
  const rel = rows[idx];
  if (!rel) return preview;

  const { sourceType, targetType, canValidate } = effectiveEndpointTypesForValidation(
    rel,
    overrides,
    preview,
    editingEntity
  );
  if (!canValidate) return preview;

  const r = augmentRelMissingEndpoint(rel);
  const peerTypeNorm = normalizeTypeForCompare(
    r.missing_end === "source"
      ? sourceType
      : r.missing_end === "target"
        ? targetType
        : targetType || sourceType
  );
  const flipped = shouldFlipRelationshipForPeerType(r, peerTypeNorm);
  const effSource = flipped ? targetType : sourceType;
  const effTarget = flipped ? sourceType : targetType;
  const guessedType = guessInstantRelationshipType(
    rel,
    effSource,
    effTarget,
    preview,
    editingEntity,
    flipped
  );
  const beforeType = String(rel?.relationship_type || "").trim();
  const patched = applyMetamodelReconcileToRelationship(rel, {
    sourceType: effSource,
    targetType: effTarget,
    relationshipType: guessedType,
    preview,
    editingEntity,
    flipped,
  });
  const afterType = String(patched?.relationship_type || "").trim();
  if (
    !relationshipTypeChangedForMetamodel(beforeType, afterType) &&
    normalizeTypeForCompare(rel?.missing_entity_type) ===
      normalizeTypeForCompare(patched?.missing_entity_type) &&
    !flipped
  ) {
    return preview;
  }

  const nextRows = [...rows];
  nextRows[idx] = patched;
  const outcomes = { ...(preview.enrichment_review_outcomes || {}) };
  const adjustments = { ...(outcomes.relationship_metamodel_adjustments || {}) };
  if (beforeType && afterType && beforeType !== afterType) {
    adjustments[`p${idx}`] = { from: beforeType, to: afterType, pending: true };
  }
  return {
    ...preview,
    proposed_relationships: nextRows,
    enrichment_review_outcomes: {
      ...outcomes,
      relationship_metamodel_adjustments: adjustments,
    },
  };
}

/** Indices of proposed relationships invalid for the given type overrides. */
export async function indicesInvalidForMetamodel(
  preview,
  overrides = {},
  editingEntity = {},
  relActions = {}
) {
  const { discarded } = await reconcileProposedRelationshipsForMetamodel(
    preview,
    overrides,
    editingEntity,
    relActions
  );
  return discarded;
}

/**
 * Reconcile pending proposed relationships after roster type overrides:
 * discard invalid pairs; repair valid pairs (type + direction when needed).
 *
 * @returns {{ discarded: number[], repairs: Array<{ idx, from, to, flipped }>, summary: { adjusted: number, discarded: number } }}
 */
export async function reconcileProposedRelationshipsForMetamodel(
  preview,
  overrides = {},
  editingEntity = {},
  relActions = {},
  options = {}
) {
  const onlyIndices = Array.isArray(options.onlyIndices)
    ? options.onlyIndices.filter((i) => Number.isFinite(i))
    : null;
  const onlySet = onlyIndices !== null ? new Set(onlyIndices) : null;
  const skipDiscards = Boolean(options.skipDiscards);

  const rows = Array.isArray(preview?.proposed_relationships)
    ? preview.proposed_relationships
    : [];
  let { pairs, pairIndices } = collectPendingRelationshipMetamodelPairs(
    preview,
    overrides,
    editingEntity,
    relActions
  );
  if (onlySet) {
    const filteredPairs = [];
    const filteredIdx = [];
    pairIndices.forEach((idx, i) => {
      if (!onlySet.has(idx)) return;
      filteredPairs.push(pairs[i]);
      filteredIdx.push(idx);
    });
    pairs = filteredPairs;
    pairIndices = filteredIdx;
  }
  const empty = { discarded: [], repairs: [], summary: { adjusted: 0, discarded: 0 } };
  if (onlySet && onlySet.size === 0) return empty;
  if (!pairs.length) return empty;

  const withTypes = pairs.map((p, i) => {
    const idx = pairIndices[i];
    const rel = rows[idx];
    const { sourceType, targetType } = effectiveEndpointTypesForValidation(
      rel,
      overrides,
      preview,
      editingEntity
    );
    return {
      source_type: sourceType,
      target_type: targetType,
      relationship_type: String(rel?.relationship_type || rel?.kind || "").trim() || undefined,
      _idx: idx,
      _rel: rel,
      _sourceType: sourceType,
      _targetType: targetType,
    };
  });

  let results = await reconcileEnrichmentRelationshipPairs(
    withTypes.map(({ source_type, target_type, relationship_type }) => ({
      source_type,
      target_type,
      relationship_type,
    }))
  );

  const swapCandidates = [];
  results.forEach((res, i) => {
    if (res?.valid) return;
    const row = withTypes[i];
    swapCandidates.push({
      i,
      source_type: row._targetType,
      target_type: row._sourceType,
      relationship_type: row.relationship_type,
    });
  });

  if (swapCandidates.length) {
    const swapResults = await reconcileEnrichmentRelationshipPairs(swapCandidates);
    swapCandidates.forEach((cand, j) => {
      const swapRes = swapResults[j];
      if (!swapRes?.valid) return;
      const i = cand.i;
      results[i] = { ...swapRes, _flipped: true };
    });
  }

  const discarded = [];
  const repairs = [];

  results.forEach((res, i) => {
    const idx = withTypes[i]._idx;
    const rel = withTypes[i]._rel;
    const sourceType = withTypes[i]._sourceType;
    const targetType = withTypes[i]._targetType;
    if (!res?.valid) {
      discarded.push(idx);
      return;
    }
    const flipped = Boolean(res._flipped);
    const effSource = flipped ? targetType : sourceType;
    const effTarget = flipped ? sourceType : targetType;
    const resolvedType = String(res.relationship_type || "").trim();
    if (!resolvedType) return;

    const beforeType = String(rel?.relationship_type || "").trim();
    const patched = applyMetamodelReconcileToRelationship(rel, {
      sourceType: effSource,
      targetType: effTarget,
      relationshipType: resolvedType,
      preview,
      editingEntity,
      flipped,
    });
    const afterType = String(patched?.relationship_type || "").trim();
    const typeChanged = relationshipTypeChangedForMetamodel(beforeType, afterType);
    const endTypesChanged =
      normalizeTypeForCompare(rel?.missing_entity_type) !==
        normalizeTypeForCompare(patched?.missing_entity_type) ||
      normalizeTypeForCompare(rel?.source_entity_type) !==
        normalizeTypeForCompare(patched?.source_entity_type) ||
      normalizeTypeForCompare(rel?.target_entity_type) !==
        normalizeTypeForCompare(patched?.target_entity_type);

    if (flipped || typeChanged || endTypesChanged) {
      repairs.push({
        idx,
        from: beforeType,
        to: afterType,
        flipped,
        row: patched,
      });
    }
  });

  const effectiveDiscarded = skipDiscards
    ? []
    : onlySet && discarded.length
      ? discarded.filter((idx) => !onlySet.has(idx))
      : discarded;

  const summary = {
    adjusted: repairs.length,
    discarded: effectiveDiscarded.length,
  };
  return { discarded: effectiveDiscarded, repairs, summary };
}

/** Apply reconcile API results onto a preview (shared by full + focused sync). */
export function applyMetamodelReconcileResultsToPreview(
  preview,
  { discarded = [], repairs = [] },
  relActions = {}
) {
  if (!preview) return preview;
  const rows = Array.isArray(preview.proposed_relationships)
    ? preview.proposed_relationships
    : [];
  let next = preview;
  repairs.forEach(({ idx, row, from, to }) => {
    const copy = [...(next.proposed_relationships || rows)];
    copy[idx] = row;
    next = { ...next, proposed_relationships: copy };
    if (from && to && from !== to) {
      const outcomes = { ...(next.enrichment_review_outcomes || {}) };
      const adjustments = {
        ...(outcomes.relationship_metamodel_adjustments || {}),
        [`p${idx}`]: { from, to },
      };
      next = {
        ...next,
        enrichment_review_outcomes: {
          ...outcomes,
          relationship_metamodel_adjustments: adjustments,
        },
      };
    }
  });
  if (!discarded.length) return next;
  const outcomes = { ...(next.enrichment_review_outcomes || {}) };
  const pr = { ...(outcomes.proposed_relationships || {}) };
  const metamodelDiscards = [...(outcomes.relationships_discarded_by_metamodel || [])];
  discarded.forEach((idx) => {
    const key = `p${idx}`;
    const eff = outcomes.proposed_relationships?.[key];
    if (eff === "approved" || eff === "discarded") return;
    const live = relActions[key]?.state;
    if (live === "approved" || live === "discarded") return;
    pr[key] = "discarded";
    if (!metamodelDiscards.includes(idx)) {
      metamodelDiscards.push(idx);
    }
  });
  return {
    ...next,
    enrichment_review_outcomes: {
      ...outcomes,
      proposed_relationships: pr,
      relationships_discarded_by_metamodel: metamodelDiscards,
    },
  };
}
